import { Alert, Badge, Table } from "flowbite-react";
import TitleStripe from "../TitleStripe";
import NoOcurrence from "./NoOcurrence";
import { formatToReal, getImageURLScore } from "../../../utils/utils";
import AvisoCard from "./AvisoCard";
import { useState } from "react";
import {
  HiCheckCircle,
  HiOutlineExclamationCircle,
  HiXCircle,
} from "react-icons/hi";
import moment from "moment";
import { FaChartLine, FaRegCreditCard } from "react-icons/fa";

const AvaliaRiscoCompletaPositivoPJ = ({ data, selectedQueries }) => {
  const [selectedNewQueries] = useState(selectedQueries || []);

  if (!data) {
    return (
      <>
        <TitleStripe>Avalia Risco PJ</TitleStripe>
        <Alert className="mt-2" color="gray" withBorderAccent>
          Consulta indisponível no momento!
        </Alert>
      </>
    );
  }

  const renderTable = (title, headers, rows, renderRow, resumoKey = null) => {
    const resumo =
      resumoKey && Array.isArray(rows) && rows.length > 0
        ? data?.resumoConsulta?.[resumoKey]
        : null;
    return (
      <>
        <TitleStripe>{title}</TitleStripe>
        {resumo && (
          <div className="text-sm text-gray-700 px-1 flex gap-10">
            <div>
              <strong>Quantidade Total:</strong> {resumo.quantidadeTotal || 0}
            </div>
            {"valorTotal" in resumo && (
              <div>
                <strong>Valor Total:</strong> {formatToReal(resumo.valorTotal)}
              </div>
            )}
          </div>
        )}
        {Array.isArray(rows) && rows.length > 0 ? (
          <>
            <div className="overflow-x-auto border border-gray-100 rounded-lg">
              <Table striped>
                <Table.Head className="text-black">
                  {headers.map((header, idx) => (
                    <Table.HeadCell
                      key={idx}
                      className="text-center"
                      style={{ background: "#3EED98" }}
                    >
                      {header}
                    </Table.HeadCell>
                  ))}
                </Table.Head>
                <Table.Body className="divide-y">
                  {rows.map((item, idx) => renderRow(item, idx))}
                </Table.Body>
              </Table>
            </div>
            <span className="block text-xs italic">
              Exibindo as últimas 5 ocorrências
            </span>
          </>
        ) : (
          <div className="-mt-3">
            <NoOcurrence />
          </div>
        )}
      </>
    );
  };

  const quadroSocietario = Array.isArray(data?.quadroSocietario)
    ? data.quadroSocietario
    : [];
  const diretoria = Array.isArray(data?.diretoria) ? data.diretoria : [];
  const chequesSustados = Array.isArray(data?.chequesSustados)
    ? data.chequesSustados
    : [];
  const historicoConsultas = Array.isArray(data?.ultimasConsultas)
    ? data.ultimasConsultas
    : [];

  const isMenorQue2Anos = data?.dadosCadastrais?.dataFundacao
    ? moment().diff(
        moment(data?.dadosCadastrais?.dataFundacao, "DD/MM/YYYY"),
        "years"
      ) < 2
    : false;

  return (
    <>
      {data?.dadosCadastrais && (
        <div className="mt-2 space-y-4">
          {/* Dados Cadastrais */}
          <div className="border rounded-2xl shadow p-6 bg-white">
            <h3 className="text-lg font-bold mb-4 border-b pb-2">
              📋 Dados Cadastrais
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
              <div>
                <strong>Razão Social:</strong>{" "}
                {data.dadosCadastrais.razaoSocial || "Não informado"}
              </div>
              <div>
                <strong>Nome Fantasia:</strong>{" "}
                {data.dadosCadastrais.nomeFantasia || "Não informado"}
              </div>
              <div>
                <strong>CNPJ:</strong>{" "}
                {data.dadosCadastrais.cnpj || "Não informado"}
              </div>
              <div>
                <strong>Situação CNPJ:</strong>{" "}
                {data.dadosCadastrais.situacaoCnpj || "Não informado"}
              </div>
              <div>
                <strong>Data Situação:</strong>{" "}
                {data.dadosCadastrais.dataSituacao || "Não informado"}
              </div>
              <div className="flex items-center gap-2">
                <strong>Data Fundação:</strong>{" "}
                {data.dadosCadastrais.dataFundacao || "Não informado"}
                {isMenorQue2Anos && (
                  <Badge color="warning" className="w-fit">
                    ⚠️ Data inferior a 2 anos
                  </Badge>
                )}
              </div>
              <div>
                <strong>Inscrição Estadual:</strong>{" "}
                {data.dadosCadastrais.inscricaoEstadual || "Não informado"}
              </div>
              <div>
                <strong>UF IE:</strong>{" "}
                {data.dadosCadastrais.ufInscricaoEstadual || "Não informado"}
              </div>
              <div>
                <strong>Natureza Jurídica:</strong>{" "}
                {data.dadosCadastrais.naturezaJuridica || "Não informado"}
              </div>
              <div>
                <strong>NIRE:</strong>{" "}
                {data.dadosCadastrais.numeroNIRE || "Não informado"}
              </div>
              <div>
                <strong>UF NIRE:</strong>{" "}
                {data.dadosCadastrais.ufNIRE || "Não informado"}
              </div>
              <div>
                <strong>Órgão Registro:</strong>{" "}
                {data.dadosCadastrais.orgaoRegistro || "Não informado"}
              </div>
              <div>
                <strong>Data Registro:</strong>{" "}
                {data.dadosCadastrais.dataRegistro || "Não informado"}
              </div>
              <div>
                <strong>Segmento:</strong>{" "}
                {data.dadosCadastrais.segmento || "Não informado"}
              </div>
              <div>
                <strong>Capital Inicial:</strong> R${" "}
                {data.dadosCadastrais.capitalInicial || "Não informado"}
              </div>
              <div>
                <strong>Capital Atual:</strong> R${" "}
                {data.dadosCadastrais.capitalAtual || "Não informado"}
              </div>
              <div>
                <strong>Moeda:</strong>{" "}
                {data.dadosCadastrais.moedaCapitalAtual || "Não informado"}
              </div>
              <div>
                <strong>Data Alteração Capital:</strong>{" "}
                {data.dadosCadastrais.dataAlteracaoCapital || "Não informado"}
              </div>
              <div>
                <strong>Filiais:</strong>{" "}
                {data.dadosCadastrais.filiais || "Não informado"}
              </div>
              <div>
                <strong>Qtd. Filiais:</strong>{" "}
                {data.dadosCadastrais.quantidadeFiliais ?? 0}
              </div>
            </div>
          </div>

          {/* Atividades */}
          <div className="border rounded-2xl shadow p-6 bg-white">
            <h3 className="text-lg font-bold mb-4 border-b pb-2">
              🛠️ Atividades Econômicas
            </h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
              <div>
                <strong>CNAE Principal:</strong>
                <br />
                {data.dadosCadastrais.cnaePrimario?.codigo ||
                  "Não informado"} -{" "}
                {data.dadosCadastrais.cnaePrimario?.atividade ||
                  "Descrição não informada"}
              </div>
              <div>
                <strong>CNAE Secundária:</strong>
                <br />
                {data.dadosCadastrais.cnaeSecundario?.codigo ||
                  "Não informado"}{" "}
                -{" "}
                {data.dadosCadastrais.cnaeSecundario?.atividade ||
                  "Descrição não informada"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Score */}
      <TitleStripe>Score</TitleStripe>
      <div className="my-4">
        <img
          className="mx-auto max-w-sm"
          src={getImageURLScore(+data?.score?.score)}
        />
        <div className="font-semibold text-xl text-center">
          {data?.score?.score}
        </div>
        <p className="text-center text-sm text-gray-600 max-w-xl mx-auto border border-gray-300 p-2 bg-gray-50">
          {data?.score?.mensagem}
        </p>
      </div>

      {/* {data?.decisaoAnalise?.descricao &&
        (() => {
          const flag = data.decisaoAnalise.flag;
          const descricao = data.decisaoAnalise.descricao;

          const getVisual = () => {
            switch (flag) {
              case 1:
                return {
                  icon: <HiCheckCircle className="text-green-600 w-6 h-6" />,
                  bg: "bg-green-50",
                  border: "border-green-200",
                  text: "text-green-800",
                  label: "",
                };
              case 2:
                return {
                  icon: (
                    <HiOutlineExclamationCircle className="text-yellow-600 w-6 h-6" />
                  ),
                  bg: "bg-yellow-50",
                  border: "border-yellow-200",
                  text: "text-yellow-800",
                  label: "Negociação com Atenção",
                };
              case 3:
                return {
                  icon: <HiXCircle className="text-red-600 w-6 h-6" />,
                  bg: "bg-red-50",
                  border: "border-red-200",
                  text: "text-red-800",
                  label: "Negociação Não Recomendada",
                };
              case 4:
              default:
                return {
                  icon: (
                    <HiOutlineExclamationCircle className="text-orange-600 w-6 h-6" />
                  ),
                  bg: "bg-orange-50",
                  border: "border-orange-200",
                  text: "text-orange-800",
                  label: "Negociação Cautelosa",
                };
            }
          };

          const { icon, bg, border, text, label } = getVisual();

          return (
            <div
              className={`mt-6 p-4 rounded-lg border ${border} ${bg} flex items-start gap-4`}
            >
              <div className="pt-1">{icon}</div>
              <div>
                <div className={`font-bold text-base ${text}`}>{descricao}</div>
                <p className={`text-sm ${text}`}>
                  Esta análise foi baseada na validação de informações
                  existentes na Boa Vista SCPC e é de inteira responsabilidade
                  do usuário e da empresa a decisão de aprovar/reprovar a
                  negociação. As informações são confidenciais e é proibida a
                  sua reprodução total ou parcial, bem como qualquer forma de
                  divulgação.
                </p>
              </div>
            </div>
          );
        })()} */}

      {selectedNewQueries?.includes(
        "additional-risco-faturamento-presumido"
      ) && (
        <>
          {" "}
          <div>
            <TitleStripe className="mb-4 flex items-center gap-2">
              Faturamento Presumido
            </TitleStripe>

            {data?.faturamentoPresumido?.anual ? (
              <div className="flex flex-col md:flex-row gap-4 items-start md:items-center bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition">
                <div className="flex items-center gap-4">
                  <div className="bg-green-100 text-green-700 p-3 rounded-full">
                    <FaChartLine className="text-2xl" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-800 w-[250px]">
                      {data?.faturamentoPresumido?.anual}
                    </div>
                    <div className="text-xs text-gray-400">Estimado ao ano</div>
                  </div>
                </div>

                <div className="text-sm text-gray-600 mt-2 md:mt-0 md:ml-6 flex-1">
                  <div className="font-medium text-gray-700 mb-1">
                    Metodologia aplicada:
                  </div>
                  <p className="text-justify text-xs leading-relaxed">
                    {data?.faturamentoPresumido?.mensagem ||
                      "Não há informações comportamentais disponíveis."}
                  </p>
                </div>
              </div>
            ) : (
              <Alert color="gray">
                Sem informações suficientes para informar o faturamento
                presumido da empresa.
              </Alert>
            )}
          </div>
        </>
      )}
      {selectedNewQueries?.includes("additional-risco-limite-credito") && (
        <>
          <div>
            <TitleStripe className="mb-4 flex items-center gap-2">
              Limite de Crédito Estimado
            </TitleStripe>

            {data?.limiteCredito?.score ? (
              <div className="flex flex-col md:flex-row gap-4 items-start md:items-center bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition">
                <div className="flex items-center gap-4">
                  <div className="bg-blue-100 text-blue-600 p-3 rounded-full">
                    <FaRegCreditCard className="text-2xl" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-700 w-[250px]">
                      {formatToReal(data.limiteCredito.score)}
                    </div>
                    <div className="text-xs text-gray-400">
                      {data?.limiteCredito?.texto ||
                        "Limite de Crédito Estimado"}
                    </div>
                  </div>
                </div>

                <div className="text-sm text-gray-600 mt-2 md:mt-0 md:ml-6 flex-1">
                  <div className="font-medium text-gray-700 mb-1">
                    Observação Importante:
                  </div>
                  <p className="text-justify text-xs leading-relaxed">
                    IMPORTANTE: a definição do valor do limite de crédito
                    efetivamente concedido é de total responsabilidade da
                    empresa cedente e deve estar alinhado a sua política interna
                    de concessão de crédito.
                  </p>
                </div>
              </div>
            ) : (
              <Alert color="gray">
                Sem informações suficientes para informar o limite de crédito
                sugerido.
              </Alert>
            )}
          </div>
        </>
      )}
      {/* Quadro Societário */}
      {selectedNewQueries?.includes("additional-risco-quadro-social-plus") && (
        <>
          <TitleStripe>Quadro Societário</TitleStripe>
          <div className="flex flex-col gap-4">
            {quadroSocietario.map((item, idx) => {
              const r = item.restricoes || {};
              return (
                <div
                  key={idx}
                  className="bg-white border border-gray-200 rounded-lg px-4 py-3 shadow-sm text-sm"
                >
                  {/* Dados principais: horizontal e compacto */}
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-gray-700 mb-2">
                    <div>
                      <span className="font-medium">Nome:</span> {item.nome}
                    </div>
                    <div>
                      <span className="font-medium">Documento:</span>{" "}
                      {item.documento}
                    </div>
                    <div>
                      <span className="font-medium">Participação:</span>{" "}
                      {item.participacao}%
                    </div>
                    <div>
                      <span className="font-medium">Entrada:</span>{" "}
                      {item.dataEntrada}
                    </div>
                    <div>
                      <span className="font-medium">Assina:</span>{" "}
                      <span
                        className={
                          item.assinaEmpresa
                            ? "text-green-600 font-semibold"
                            : "text-gray-400"
                        }
                      >
                        {item.assinaEmpresa ? "Sim" : "Não"}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Débito:</span>{" "}
                      <span
                        className={
                          item.temDebito
                            ? "text-red-600 font-semibold"
                            : "text-gray-400"
                        }
                      >
                        {item.temDebito ? "Sim" : "Não"}
                      </span>
                    </div>
                  </div>

                  {/* Restrições: mini cards estreitos */}
                  <div className="flex flex-col gap-2">
                    {r.pendenciasFinanceiras?.quantidade > 0 && (
                      <div className="bg-red-50 border-l-2 border-red-400 px-3 py-2 rounded">
                        <div className="font-semibold text-red-700 text-xs mb-1">
                          Pendências Financeiras
                        </div>
                        <div className="text-xs text-gray-700">
                          {r.pendenciasFinanceiras.quantidade} ocorrências — R${" "}
                          {r.pendenciasFinanceiras.valor} — Última:{" "}
                          {r.pendenciasFinanceiras.ultima}
                        </div>
                      </div>
                    )}
                    {r.protestos?.quantidade > 0 && (
                      <div className="bg-yellow-50 border-l-2 border-yellow-400 px-3 py-2 rounded">
                        <div className="font-semibold text-yellow-700 text-xs mb-1">
                          Protestos
                        </div>
                        <div className="text-xs text-gray-700">
                          {r.protestos.quantidade} ocorrências — R${" "}
                          {r.protestos.valor}
                        </div>
                      </div>
                    )}
                    {r.acoesJudiciais?.quantidade > 0 && (
                      <div className="bg-purple-50 border-l-2 border-purple-400 px-3 py-2 rounded">
                        <div className="font-semibold text-purple-700 text-xs mb-1">
                          Ações Judiciais
                        </div>
                        <div className="text-xs text-gray-700">
                          {r.acoesJudiciais.quantidade} ocorrências — R${" "}
                          {r.acoesJudiciais.valor}
                          {r.acoesJudiciais.ultima &&
                            ` — Última: ${r.acoesJudiciais.ultima}`}
                        </div>
                      </div>
                    )}
                    {r.chequesSemFundo?.quantidade > 0 && (
                      <div className="bg-blue-50 border-l-2 border-blue-400 px-3 py-2 rounded">
                        <div className="font-semibold text-blue-700 text-xs mb-1">
                          Cheques Sem Fundo
                        </div>
                        <div className="text-xs text-gray-700">
                          {r.chequesSemFundo.quantidade} ocorrências — R${" "}
                          {r.chequesSemFundo.valor}
                        </div>
                      </div>
                    )}
                    {r.falenciasRecuperacao?.quantidade > 0 && (
                      <div className="bg-gray-100 border-l-2 border-gray-500 px-3 py-2 rounded">
                        <div className="font-semibold text-gray-700 text-xs mb-1">
                          Falência / Recuperação
                        </div>
                        <div className="text-xs text-gray-700">
                          {r.falenciasRecuperacao.quantidade} ocorrências — R${" "}
                          {r.falenciasRecuperacao.valor}
                        </div>
                      </div>
                    )}
                    {[
                      r.pendenciasFinanceiras?.quantidade,
                      r.protestos?.quantidade,
                      r.acoesJudiciais?.quantidade,
                      r.chequesSemFundo?.quantidade,
                      r.falenciasRecuperacao?.quantidade,
                    ].every((qtd) => !qtd || qtd === 0) && (
                      <div className="text-xs italic text-gray-400">
                        Sem restrições registradas
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Diretoria */}
      {selectedNewQueries?.includes("additional-risco-quadro-social-plus") &&
        renderTable(
          "Diretoria / Administradores",
          ["Nome", "Documento", "Cargo", "Desde", "Tem Débito?"],
          diretoria,
          (item, idx) => (
            <Table.Row key={idx} className="bg-white text-black">
              <Table.Cell className="text-center py-1">{item.nome}</Table.Cell>
              <Table.Cell className="text-center py-1">
                {item.documento}
              </Table.Cell>
              <Table.Cell className="text-center py-1">{item.cargo}</Table.Cell>
              <Table.Cell className="text-center py-1">{item.desde}</Table.Cell>
              <Table.Cell className="text-center py-1">
                {item.temDebito ? "Sim" : "Não"}
              </Table.Cell>
            </Table.Row>
          )
        )}
      {selectedNewQueries?.includes(
        "additional-risco-participacoes-empresas"
      ) && (
        <>
          <TitleStripe>Participações dos Sócios em Outras Empresas</TitleStripe>

          {Array.isArray(data?.participacoesSocios) &&
          data.participacoesSocios.length > 0 ? (
            <div className="flex flex-col gap-4">
              {data.participacoesSocios.map((socio, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-md p-4 text-sm bg-white shadow-sm"
                >
                  <div className="font-semibold text-base mb-2">
                    {socio.nomeSocio} - {socio.documentoSocio}
                  </div>

                  {socio.empresas.length > 0 ? (
                    socio.empresas.map((empresa, idx) => {
                      const restricoes = empresa.restricoes || {};

                      const parseValor = (valor) =>
                        parseFloat(
                          (valor || "0").replace(/\./g, "").replace(",", ".")
                        );

                      const totalPendencias =
                        parseValor(restricoes?.protestos?.valor) +
                        parseValor(restricoes?.acoesJudiciais?.valor) +
                        parseValor(restricoes?.pendenciasFinanceiras?.valor) +
                        parseValor(restricoes?.chequesSemFundo?.valor) +
                        parseValor(restricoes?.falenciasRecuperacao?.valor);

                      return (
                        <div
                          key={idx}
                          className="border border-gray-100 rounded-md p-3 mb-3 bg-gray-50"
                        >
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <strong>Empresa:</strong> {empresa.razaoSocial}
                            </div>
                            <div>
                              <strong>CNPJ:</strong> {empresa.cnpj}
                            </div>
                            <div>
                              <strong>Situação:</strong> {empresa.situacao}
                            </div>
                            <div>
                              <strong>Participação:</strong>{" "}
                              {empresa.participacao}%
                            </div>
                            <div>
                              <strong>Data de Entrada:</strong>{" "}
                              {empresa.entrada}
                            </div>
                            <div>
                              <strong>Débitos:</strong>{" "}
                              {empresa.temDebito ? "Sim" : "Não"}
                            </div>
                            <div>
                              <strong>Fraude:</strong>{" "}
                              {empresa.temFraude ? "Sim" : "Não"}
                            </div>
                            <div>
                              <strong>Protestos:</strong>{" "}
                              {restricoes?.protestos?.quantidade ?? "-"}
                            </div>
                            <div>
                              <strong>Ações Judiciais:</strong>{" "}
                              {restricoes?.acoesJudiciais?.quantidade ?? "-"}
                            </div>
                            <div>
                              <strong>Pendências Financeiras:</strong>{" "}
                              {restricoes?.pendenciasFinanceiras?.quantidade ??
                                "-"}
                            </div>
                            <div>
                              <strong>Cheques Sem Fundo:</strong>{" "}
                              {restricoes?.chequesSemFundo?.quantidade ?? "-"}
                            </div>
                            <div>
                              <strong>Falência / Recuperação:</strong>{" "}
                              {restricoes?.falenciasRecuperacao?.quantidade ??
                                "-"}
                            </div>
                            {totalPendencias > 0 && (
                              <div className="col-span-2 text-red-600 font-semibold">
                                Valor Total de Pendências:{" "}
                                {formatToReal(totalPendencias)}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-gray-500 italic text-sm">
                      Nenhuma empresa encontrada para este sócio.
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <Alert color="gray">Nenhuma participação encontrada.</Alert>
          )}
        </>
      )}

      {/* Histórico de Consultas */}
      {renderTable(
        "Histórico de Consultas",
        ["Data", "Empresa"],
        historicoConsultas,
        (item, idx) => (
          <Table.Row key={idx} className="bg-white text-black">
            <Table.Cell className="text-center py-1">{item.data}</Table.Cell>
            <Table.Cell className="text-center py-1">{item.empresa}</Table.Cell>
          </Table.Row>
        )
      )}

      <TitleStripe>Compromissos Futuros</TitleStripe>
      {data?.comprometimentoFuturo?.compromissos?.length > 0 ? (
        <div className="overflow-x-auto border border-gray-100 rounded-lg">
          <Table striped>
            <Table.Head className="text-black">
              {(data?.comprometimentoFuturo?.compromissos || []).map(
                (item, idx) => (
                  <Table.HeadCell
                    key={idx}
                    className="text-center"
                    style={{ background: "#3EED98" }}
                  >
                    {item.periodo}
                  </Table.HeadCell>
                )
              )}
            </Table.Head>
            <Table.Body>
              <Table.Row className="bg-white text-black">
                {(data?.comprometimentoFuturo?.compromissos || []).map(
                  (item, idx) => (
                    <Table.Cell key={idx} className="text-center py-1">
                      {item.pontuacao}
                    </Table.Cell>
                  )
                )}
              </Table.Row>
            </Table.Body>
          </Table>
        </div>
      ) : (
        <Alert color="gray">
          Nenhuma informação dos compromissos futuros para esta empresa.
        </Alert>
      )}

      {selectedNewQueries?.includes(
        "additional-risco-pagamentos-fornecedores"
      ) && (
        <>
          <TitleStripe>Comportamento de Pagamento</TitleStripe>
          {data?.pagamentoPontual?.indicadores?.length > 0 ? (
            <div>
              <div className="overflow-x-auto border border-gray-100 rounded-lg mb-6">
                <Table striped>
                  <Table.Head className="text-black">
                    <Table.HeadCell
                      className="text-center font-bold px-1"
                      style={{ background: "#3EED98" }}
                    >
                      Tipo
                    </Table.HeadCell>
                    {data.pagamentoPontual.indicadores.map((item, idx) => (
                      <Table.HeadCell
                        key={`mesAno-${idx}`}
                        className="text-center px-1"
                        style={{ background: "#3EED98" }}
                      >
                        {item.mesAno}
                      </Table.HeadCell>
                    ))}
                  </Table.Head>
                  <Table.Body>
                    {[
                      { key: "pontuacao", label: "Pontual" },
                      { key: "periodo6a15", label: "Atraso 6-15 dias" },
                      { key: "periodo16a30", label: "Atraso 16-30 dias" },
                      { key: "periodo31a60", label: "Atraso 31-60 dias" },
                      { key: "mais60", label: "Atraso +60 dias" },
                      { key: "atrasoMedio", label: "Atraso Médio" },
                    ].map(({ key, label }) => (
                      <Table.Row key={key} className="bg-white text-black">
                        <Table.Cell className="text-center py-1 font-medium px-1">
                          {label}
                        </Table.Cell>
                        {data.pagamentoPontual.indicadores.map((item, idx) => (
                          <Table.Cell
                            key={`${key}-${idx}`}
                            className="text-center py-1 px-1"
                          >
                            {item[key] !== undefined && item[key] !== null
                              ? typeof item[key] === "number"
                                ? `${item[key]}%`
                                : item[key]
                              : "–"}
                          </Table.Cell>
                        ))}
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>

              <div className="flex justify-center items-center mt-3">
                <span className="text-green-500 font-semibold">100%</span> :
                Pagamentos Pontuais |{" "}
                <span className="text-red-500 font-semibold ml-1">0%</span>:
                Pagamentos não pontuais
              </div>
            </div>
          ) : (
            <Alert color="gray">
              Nenhuma informação dos compromissos futuros para esta empresa.
            </Alert>
          )}
        </>
      )}

      <TitleStripe>Pagamento Atrasado</TitleStripe>
      {data?.pagamentoAtrasado?.periodo6a15?.length > 1 ? (
        <div>
          <div className="overflow-x-auto border border-gray-100 rounded-lg mb-6">
            <Table striped>
              <Table.Head className="text-black">
                <Table.HeadCell
                  className="text-center font-bold px-1"
                  style={{ background: "#E2E8F0" }}
                >
                  Faixa
                </Table.HeadCell>
                {data.pagamentoAtrasado.periodo6a15
                  .slice(1)
                  .map((item, idx) => (
                    <Table.HeadCell
                      key={`mesAno-${idx}`}
                      className="text-center px-1"
                      style={{ background: "#E2E8F0" }}
                    >
                      {item.mesAno}
                    </Table.HeadCell>
                  ))}
              </Table.Head>
              <Table.Body>
                {[
                  { key: "periodo6a15", label: "6-15 dias" },
                  { key: "periodo16a30", label: "16-30 dias" },
                  { key: "periodo31a60", label: "31-60 dias" },
                  { key: "mais60", label: "+60 dias" },
                  { key: "atrasoMedio", label: "Atraso médio" },
                ].map(({ key, label }) => (
                  <Table.Row key={key} className="bg-white text-black">
                    <Table.Cell className="text-center py-1 font-medium px-1">
                      {label}
                    </Table.Cell>
                    {data.pagamentoAtrasado[key]?.slice(1).map((item, idx) => (
                      <Table.Cell
                        key={`${key}-${idx}`}
                        className="text-center py-1 px-1"
                      >
                        {item.pontuacao !== undefined && item.pontuacao !== null
                          ? item.pontuacao === "-"
                            ? "-"
                            : typeof item.pontuacao === "number"
                            ? `${item.pontuacao}`
                            : item.pontuacao
                          : "–"}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        </div>
      ) : (
        <Alert color="gray">
          Nenhum dado de pagamento atrasado disponível.
        </Alert>
      )}

      <>
        <TitleStripe>Crédito Obtido (Pontuação Global)</TitleStripe>
        {data?.creditoObtidoDetalhado?.historico?.length > 0 ? (
          <div className="gap-8">
            {data?.creditoObtidoDetalhado?.pontuacaoTotal && (
              <div>
                <div className="text-sm font-semibold text-gray-700">
                  Pontuação Total:
                </div>
                <div className="text-2xl font-bold text-blue-700">
                  {data.creditoObtidoDetalhado.pontuacaoTotal}
                </div>
              </div>
            )}

            <div>
              <span className="block mt-2 text-sm text-gray-700">
                O objetivo desse indicador é apresentar a pontuação de
                comprometimento (em quantidade de reais) da empresa consultada
                por mês e a somatória dos últimos 12 meses.
              </span>
            </div>

            {data?.creditoObtidoDetalhado?.historico?.length > 0 && (
              <div className="mt-6 rounded-lg border border-gray-200 shadow-sm overflow-auto">
                <Table>
                  <Table.Head className="bg-gray-100 text-gray-900">
                    {data.creditoObtidoDetalhado.historico.map((item, idx) => (
                      <Table.HeadCell
                        key={idx}
                        className="text-center font-medium whitespace-nowrap"
                        style={{ background: "#3EED98" }}
                      >
                        {item.mesAno}
                      </Table.HeadCell>
                    ))}
                  </Table.Head>
                  <Table.Body>
                    <Table.Row className="hover:bg-gray-50 transition">
                      {data.creditoObtidoDetalhado.historico.map(
                        (item, idx) => (
                          <Table.Cell
                            key={idx}
                            className={`text-center font-semibold text-sm`}
                          >
                            {item.pontuacaoMensal}
                          </Table.Cell>
                        )
                      )}
                    </Table.Row>
                  </Table.Body>
                </Table>
              </div>
            )}
          </div>
        ) : (
          <Alert color="gray">Nenhuma informação de crédito obtido.</Alert>
        )}
      </>

      <TitleStripe>Créditos Futuros</TitleStripe>
      {data?.comprometimentoFuturo?.creditos?.length > 0 ? (
        <div className="overflow-x-auto border border-gray-100 rounded-lg">
          <Table striped>
            <Table.Head className="text-black">
              {(data?.comprometimentoFuturo?.creditos || []).map(
                (item, idx) => (
                  <Table.HeadCell
                    key={idx}
                    className="text-center"
                    style={{ background: "#3EED98" }}
                  >
                    {item.periodo}
                  </Table.HeadCell>
                )
              )}
            </Table.Head>
            <Table.Body>
              <Table.Row className="bg-white text-black">
                {(data?.comprometimentoFuturo?.creditos || []).map(
                  (item, idx) => (
                    <Table.Cell
                      key={idx}
                      className="text-center py-1 font-semibold"
                    >
                      {!isNaN(item.pontuacao) ? item.pontuacao * 100 : "–"}
                    </Table.Cell>
                  )
                )}
              </Table.Row>
            </Table.Body>
          </Table>
        </div>
      ) : (
        <Alert color="gray">
          Nenhuma informação dos compromissos futuros para esta empresa.
        </Alert>
      )}

      {selectedNewQueries?.includes(
        "additional-risco-pagamentos-fornecedores"
      ) && (
        <>
          <TitleStripe>Tempo de Relacionamento com Fornecedores</TitleStripe>
          <div className="border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <Table striped>
              <Table.Head className="bg-gray-300 text-gray-900">
                <Table.HeadCell className="text-center bg-gray-300">
                  Período
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-300">
                  Quantidade
                </Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {data?.tempoRelacionamentoFornecedores?.historico?.map(
                  (item, idx) => (
                    <Table.Row key={idx} className="hover:bg-gray-50">
                      <Table.Cell className="text-center py-2 text-sm font-medium text-gray-700">
                        {item.periodo}
                      </Table.Cell>
                      <Table.Cell className="text-center py-2 text-sm text-gray-800">
                        {item.valor}
                      </Table.Cell>
                    </Table.Row>
                  )
                )}
              </Table.Body>
            </Table>
          </div>

          <TitleStripe>Acontecimentos importantes</TitleStripe>
          <Table striped>
            <Table.Head>
              <Table.HeadCell className="bg-gray-300">Período</Table.HeadCell>
              <Table.HeadCell className="bg-gray-300">Indicador</Table.HeadCell>
              <Table.HeadCell className="bg-gray-300 text-center">
                Pontuação
              </Table.HeadCell>
            </Table.Head>
            <Table.Body>
              {data.acontecimentosImportantes?.map((item, idx) => (
                <Table.Row key={idx}>
                  <Table.Cell>{item.periodo}</Table.Cell>
                  <Table.Cell>{item.indicador}</Table.Cell>
                  <Table.Cell className="text-center">
                    {item.pontuacao}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}
      {/* Quadro de Avisos */}
      <TitleStripe>Quadro de avisos</TitleStripe>
      <div className="container-alerts three-columns">
        {["pendenciasFinanceiras", "chequesSemFundo", "chequesSustados"].map(
          (key) => (
            <AvisoCard
              key={key}
              title={key
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase())}
              hasRestrictions={
                +data?.resumoConsulta?.[key]?.quantidadeTotal > 0
              }
              messageError={`${
                data?.resumoConsulta?.[key]?.quantidadeTotal || 0
              } ocorrências`}
            />
          )
        )}

        <AvisoCard
          title="Protestos"
          hasRestrictions={
            +data?.resumoConsulta?.protestos?.quantidadeTotal > 0
          }
          messageError={`${
            data?.resumoConsulta?.protestos?.quantidadeTotal || 0
          } ocorrências`}
        />

        <AvisoCard
          title="Ações Cíveis"
          hasRestrictions={+data?.acoesCiveis?.total > 0}
          messageError={`${data.acoesCiveis.total} ocorrência${
            data.acoesCiveis.total > 1 ? "s" : ""
          }`}
        />

        <AvisoCard
          title="Falência/Rec. Judicial"
          hasRestrictions={
            +data.falenciasRecuperacaoJudicial.quantidadeTotal > 0
          }
          messageError={`${data.falenciasRecuperacaoJudicial.quantidadeTotal} ocorrências`}
        />
      </div>

      {/* Protestos, Pendências, Cheques */}

      {renderTable(
        "Pendências Financeiras",
        ["Data", "Informante", "Valor", "Tipo", "Doc. Origem"],
        data?.pendenciasFinanceiras ?? [],
        (item, idx) => (
          <Table.Row key={idx} className="bg-white text-black">
            <Table.Cell className="text-center py-1">{item.data}</Table.Cell>
            <Table.Cell className="text-center py-1">
              {item.informante}
            </Table.Cell>
            <Table.Cell className="text-center py-1">
              {formatToReal(item.valor)}
            </Table.Cell>
            <Table.Cell className="text-center py-1">
              {item.tipoPendencia}
            </Table.Cell>
            <Table.Cell className="text-center py-1">
              {item.documentoOrigem}
            </Table.Cell>
          </Table.Row>
        ),
        "pendenciasFinanceiras"
      )}

      {renderTable(
        "Protestos",
        ["Data", "Cartório", "Valor", "Cidade", "Estado"],
        data?.protestos ?? [],
        (item, idx) => (
          <Table.Row key={idx} className="bg-white text-black">
            <Table.Cell className="text-center py-1">{item.data}</Table.Cell>
            <Table.Cell className="text-center py-1">
              {item.cartorio}
            </Table.Cell>
            <Table.Cell className="text-center py-1">{item.valor}</Table.Cell>
            <Table.Cell className="text-center py-1">{item.cidade}</Table.Cell>
            <Table.Cell className="text-center py-1">{item.estado}</Table.Cell>
          </Table.Row>
        ),
        "protestos"
      )}

      {renderTable(
        "Ações Cíveis",
        [
          "Autor",
          "Requerido",
          "Valor",
          "Ação",
          "Documento",
          "Foro",
          "UF",
          "Vara",
          "Distribuição",
          "Processo",
        ],
        data?.acoesCiveis?.ultimasOcorrencias || [],
        (item, idx) => (
          <Table.Row key={idx} className="bg-white text-black">
            <Table.Cell className="text-center py-1">{item.autor}</Table.Cell>
            <Table.Cell className="text-center py-1">
              {item.requerido}
            </Table.Cell>
            <Table.Cell className="text-center py-1">
              R$ {item.valor}
            </Table.Cell>
            <Table.Cell className="text-center py-1">{item.tipo}</Table.Cell>
            <Table.Cell className="text-center py-1">
              {item.documento}
            </Table.Cell>
            <Table.Cell className="text-center py-1">{item.foro}</Table.Cell>
            <Table.Cell className="text-center py-1">{item.uf}</Table.Cell>
            <Table.Cell className="text-center py-1">{item.vara}</Table.Cell>
            <Table.Cell className="text-center py-1">
              {item.distribuicao}
            </Table.Cell>
            <Table.Cell className="text-center py-1">
              {item.processo}
            </Table.Cell>
          </Table.Row>
        ),
        "acoesCiveis"
      )}

      <div>
        <TitleStripe>Falências e Recuperações Judiciais</TitleStripe>
        {data.falenciasRecuperacaoJudicial.ultimasOcorrencias?.length > 0 ? (
          <>
            <div className="text-sm text-gray-700 px-1 flex gap-10 mb-2">
              <div>
                <strong>Quantidade Total:</strong>{" "}
                {data.falenciasRecuperacaoJudicial.quantidadeTotal || 0}
              </div>
              <div>
                <strong>Período:</strong>{" "}
                {data.falenciasRecuperacaoJudicial.periodoInicial} até{" "}
                {data.falenciasRecuperacaoJudicial.periodoFinal}
              </div>
            </div>
            <div className="overflow-x-auto border border-gray-100 rounded-lg">
              <Table striped>
                <Table.Head className="text-black">
                  {["Tipo", "Data", "Cidade", "UF", "Vara"].map(
                    (header, idx) => (
                      <Table.HeadCell
                        key={idx}
                        className="text-center"
                        style={{ background: "#3EED98" }}
                      >
                        {header}
                      </Table.HeadCell>
                    )
                  )}
                </Table.Head>
                <Table.Body>
                  {data.falenciasRecuperacaoJudicial.ultimasOcorrencias.map(
                    (item, idx) => (
                      <Table.Row key={idx} className="bg-white text-black">
                        <Table.Cell className="text-center py-1">
                          {item.tipo}
                        </Table.Cell>
                        <Table.Cell className="text-center py-1">
                          {item.data}
                        </Table.Cell>
                        <Table.Cell className="text-center py-1">
                          {item.cidade}
                        </Table.Cell>
                        <Table.Cell className="text-center py-1">
                          {item.uf}
                        </Table.Cell>
                        <Table.Cell className="text-center py-1">
                          {item.vara}
                        </Table.Cell>
                      </Table.Row>
                    )
                  )}
                </Table.Body>
              </Table>
            </div>
            <span className="block text-xs italic">
              Exibindo as últimas 5 ocorrências
            </span>
          </>
        ) : (
          <NoOcurrence />
        )}
      </div>

      {renderTable(
        "Cheques Sem Fundo",
        ["Data", "Banco", "Agência", "Motivo", "Qtd"],
        data?.chequesSemFundo ?? [],
        (item, idx) => (
          <Table.Row key={idx} className="bg-white text-black">
            <Table.Cell className="text-center py-1">{item.data}</Table.Cell>
            <Table.Cell className="text-center py-1">{item.banco}</Table.Cell>
            <Table.Cell className="text-center py-1">{item.agencia}</Table.Cell>
            <Table.Cell className="text-center py-1">{item.motivo}</Table.Cell>
            <Table.Cell className="text-center py-1">{item.valor}</Table.Cell>
          </Table.Row>
        ),
        "chequesSemFundo"
      )}
      {renderTable(
        "Cheques Sustados",
        ["Mensagem"],
        chequesSustados,
        (item, idx) => (
          <Table.Row key={idx} className="bg-white text-black">
            <Table.Cell className="text-center py-1">
              {item.mensagem}
            </Table.Cell>
          </Table.Row>
        ),
        "chequesSustados"
      )}
    </>
  );
};

export default AvaliaRiscoCompletaPositivoPJ;
